import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SlidersHorizontal, Tag, MapPin } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { Pagination } from "../components/Pagination";
import { useTerms } from "../lib/useTerms";
import { useListingImage } from "../lib/termImages";

const STATES = ["", "Florida", "Alabama", "Mississippi", "Louisiana", "Texas"];

export function DealGridCard({ d }) {
  const fb = useListingImage(d, "deal");
  const img = d.image ? fileUrl(d.image) : fb.src;
  return (
    <Link to={`/deals/${d.slug || d.id}`} className="group block rounded-xl overflow-hidden bg-[#111] border border-white/10 card-hover" data-testid={`deal-card-${d.slug || d.id}`}>
      <div className="relative aspect-square overflow-hidden bg-white/5">
        {img ? <img loading="lazy" src={img} alt={d.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center"><Tag size={28} className="text-zinc-700" /></div>}
        {d.discount_percentage && <span className="absolute top-3 left-3 bg-rose-500 text-white font-black text-xs rounded-lg px-2.5 py-1">-{d.discount_percentage}%</span>}
        {d.promo_code && <span className="absolute top-3 right-3 bg-black/70 backdrop-blur rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-300 flex items-center gap-1" data-testid={`promo-badge-${d.slug || d.id}`}><Tag size={10} /> Promo</span>}
        {d.featured && !d.promo_code && <span className="absolute top-3 right-3 glass rounded-full px-3 py-1 text-xs font-bold">Featured</span>}
        {d.status === "sold_out" && <span className="absolute bottom-3 left-3 bg-black/70 rounded-full px-3 py-1 text-xs font-bold text-rose-300">Sold out</span>}
      </div>
      <div className="p-4">
        {d.category && <p className="overline mb-1">{d.category}</p>}
        <h3 className="font-display font-bold text-lg leading-tight line-clamp-2 group-hover:text-rose-400 transition-colors">{d.title}</h3>
        <p className="text-sm text-zinc-400 mt-1 flex items-center gap-1 truncate"><MapPin size={12} /> {d.venue_name}{d.city ? `, ${d.city}` : ""}</p>
        <div className="flex items-end justify-between mt-3">
          <div className="flex items-end gap-2">
            {d.discounted_price && <span className="font-display font-black text-xl text-rose-400">${d.discounted_price}</span>}
            {d.original_price && <span className="text-zinc-500 line-through text-sm mb-0.5">${d.original_price}</span>}
          </div>
          {d.end_datetime && d.status === "active" && <span className="text-[11px] text-zinc-500">ends {new Date(d.end_datetime).toLocaleDateString()}</span>}
        </div>
      </div>
    </Link>
  );
}

export default function Deals() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [f, setF] = useState({ q: "", state: "", city: "", category: "", sort: "featured", featured: false });
  const categories = useTerms("deal", "deal_category", []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      const p = new URLSearchParams({ page, limit: 12, sort: f.sort });
      ["q", "state", "city", "category"].forEach((k) => { if (f[k]) p.set(k, f[k]); });
      if (f.featured) p.set("featured", "true");
      try { const { data } = await api.get(`/deals?${p.toString()}`); setData(data); }
      catch (e) { console.error("Failed to load deals:", e); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [f, page]);

  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };
  const sel = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-rose-500";

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10" data-testid="deals-page">
      <p className="overline mb-2">Limited-time offers across the coast</p>
      <h1 className="font-display font-extrabold text-4xl md:text-5xl mb-8">Deals</h1>
      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-20 bg-[#111] border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 font-semibold"><SlidersHorizontal size={16} /> Filters</div>
            <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search deals" className={sel} data-testid="dfilter-q" />
            <select value={f.state} onChange={(e) => set("state", e.target.value)} className={sel} data-testid="dfilter-state">{STATES.map((s) => <option key={s} value={s}>{s || "All States"}</option>)}</select>
            <input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className={sel} data-testid="dfilter-city" />
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className={sel} data-testid="dfilter-category"><option value="">All Categories</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <select value={f.sort} onChange={(e) => set("sort", e.target.value)} className={sel} data-testid="dfilter-sort">
              <option value="featured">Expiring soon</option><option value="alpha">A to Z</option><option value="newest">Newest</option>
            </select>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="dfilter-featured" /> Featured only</label>
          </div>
        </aside>
        <div className="lg:col-span-9">
          <p className="text-sm text-zinc-400 mb-5">{loading ? "Loading..." : `${data.total} active deals`}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">{data.items.map((d) => <DealGridCard key={d.id} d={d} />)}</div>
          {!loading && data.items.length === 0 && <p className="text-zinc-500 py-20 text-center">No active deals match.</p>}
          <Pagination page={page} pages={data.pages} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}
